import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Loader2, RefreshCw, Send, X } from "lucide-react";
import { DeleteButton } from "./DeleteButton";
import { Pill } from "@/components/ambassadors/SalesTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const field = "h-10 px-3 rounded-lg bg-[#ffffff] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors w-full";
const label = "font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a] block mb-1";
const btn = "h-10 px-4 rounded-full font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors flex items-center gap-2 disabled:opacity-50";
const err = (e, fb) => { const d = e?.response?.data?.detail; return (Array.isArray(d) ? d[0]?.msg : d) || fb; };

const Info = ({ k, v }) => v ? <div><p className={label}>{k}</p><p className="text-sm break-words">{v}</p></div> : null;

/** Full ambassador record: profile, rate control, commission ledger with margins, payouts. */
export const AmbassadorDetail = ({ id, headers, onClose, onChanged, onDeleted }) => {
  const [a, setA] = useState(null);
  const [pct, setPct] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmPay, setConfirmPay] = useState(false);

  const load = async () => {
    const r = await axios.get(`${API}/admin/ambassadors/${id}`, { headers });
    setA(r.data); setPct(r.data.commission_pct ?? ""); setNotes(r.data.notes || "");
  };
  useEffect(() => { load().catch(() => toast.error("Could not load ambassador")); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (body, key) => {
    setBusy(key);
    try { await axios.patch(`${API}/admin/ambassadors/${id}`, body, { headers }); await load(); onChanged(); toast.success("Saved"); }
    catch (e) { toast.error(err(e, "Update failed")); }
    finally { setBusy(""); }
  };
  const setCommission = async (comId, status) => {
    setBusy(comId);
    try { await axios.patch(`${API}/admin/ambassadors/commissions/${comId}`, { status }, { headers }); await load(); onChanged(); }
    catch (e) { toast.error(err(e, "Update failed")); }
    finally { setBusy(""); }
  };
  const payout = async () => {
    setBusy("payout");
    try {
      const r = await axios.post(`${API}/admin/ambassadors/${id}/payout`, { note: "" }, { headers });
      toast.success(`Payout of ${money(r.data.amount_cents)} sent (PayPal batch ${r.data.paypal_batch_id}).`);
      setConfirmPay(false);
      await load(); onChanged();
    } catch (e) { toast.error(err(e, "Payout failed")); }
    finally { setBusy(""); }
  };
  const refreshPayout = async (pid) => {
    setBusy(pid);
    try { await axios.post(`${API}/admin/ambassadors/payouts/${pid}/refresh`, {}, { headers }); await load(); }
    catch (e) { toast.error(err(e, "Could not refresh")); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/admin/ambassadors/${id}`, { headers }); toast.success("Ambassador removed"); onDeleted(); }
    catch (e) { toast.error(err(e, "Delete failed")); }
  };

  if (!a) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>;
  const link = `${window.location.origin}/?ref=${a.code}`;
  return (
    <div data-testid="ambassador-detail" className="rounded-3xl border border-[#e3e2dc] bg-[#ffffff] p-5 sm:p-7 space-y-7">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">Ambassador · {a.code}</p>
          <h3 className="mt-1 font-display font-extrabold uppercase text-2xl leading-none">{a.name}</h3>
          <p className="mt-2 text-sm text-[#5f5e5a]">{a.email}{a.phone ? ` · ${a.phone}` : ""} · {[a.city, a.country].filter(Boolean).join(", ")}</p>
          <p className="mt-1 text-xs text-[#5f5e5a]">Joined {fmt(a.created_at)} · last sign-in {fmt(a.last_login)} · {a.clicks || 0} link clicks</p>
        </div>
        <button type="button" data-testid="ambassador-detail-close" onClick={onClose} className="h-9 w-9 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] flex items-center justify-center"><X size={14} /></button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Available", a.stats.approved_cents, "amb-detail-available"], ["Pending", a.stats.pending_cents, "amb-detail-pending"], ["Paid out", a.stats.paid_cents, "amb-detail-paid"], ["Lifetime", a.stats.lifetime_cents, "amb-detail-lifetime"]].map(([l, v, tid]) => (
          <div key={l} data-testid={tid} className="rounded-2xl bg-[#faf9f6] border border-[#e3e2dc] p-4"><p className={label}>{l}</p><p className="font-display font-extrabold text-2xl tabular-nums">{money(v)}</p></div>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Info k="PayPal (payouts)" v={a.paypal_email} />
            <Info k="Audience" v={a.audience} />
            <div className="sm:col-span-2"><p className={label}>Promotes on</p>
              <ul className="space-y-1">{(a.channels || []).map((c, i) => <li key={i} className="text-sm break-all"><span className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-[#5f5e5a] mr-2">{c.platform}</span>{/^https?:/i.test(c.handle) ? <a href={c.handle} target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-[#ff2a85] inline-flex items-center gap-1">{c.handle} <ExternalLink size={11} /></a> : c.handle}{c.followers ? <span className="text-[#5f5e5a]"> · {c.followers}</span> : null}</li>)}</ul>
            </div>
            <Info k="About" v={a.about} />
            <div className="sm:col-span-2"><p className={label}>Affiliate link</p><p className="font-mono-d text-xs break-all select-all">{link}</p></div>
          </div>
        </div>
        <div className="lg:col-span-5 space-y-4 rounded-2xl bg-[#faf9f6] border border-[#e3e2dc] p-4">
          <div><p className={label}>Status</p>
            <div className="flex flex-wrap gap-2">
              {["pending", "approved", "paused", "rejected"].map((s) => (
                <button key={s} type="button" data-testid={`amb-status-${s}`} disabled={busy === "status" || a.status === s} onClick={() => patch({ status: s }, "status")}
                  className={`${btn} border ${a.status === s ? "bg-[#0a0a0b] text-white border-[#0a0a0b]" : "border-[#e3e2dc] hover:border-[#0a0a0b]"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div><p className={label}>Commission rate (blank = program default {a.rate_pct}%)</p>
            <div className="flex gap-2">
              <input data-testid="amb-pct-input" type="number" step="0.5" min="0" max="100" className={field} value={pct} placeholder="e.g. 18" onChange={(e) => setPct(e.target.value)} />
              <button type="button" data-testid="amb-pct-save" disabled={busy === "pct"} onClick={() => patch(pct === "" ? { clear_commission_pct: true } : { commission_pct: Number(pct) }, "pct")} className={`${btn} bg-[#0a0a0b] text-white hover:bg-[#ff2a85]`}>Set</button>
            </div>
            <p className="mt-1 text-[11px] text-[#5f5e5a]">Currently earning <strong data-testid="amb-detail-rate">{a.rate_pct}%</strong> of profit per piece{a.commission_pct != null ? " (custom)" : ""}.</p>
          </div>
          <div><p className={label}>Private notes</p>
            <textarea data-testid="amb-notes" className={`${field} h-20 py-2 resize-none`} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (a.notes || "") && patch({ notes }, "notes")} />
          </div>
          {confirmPay ? (
            <div data-testid="amb-payout-confirm" className="rounded-xl border border-[#007a3d]/40 bg-[#007a3d]/5 p-3 space-y-2">
              <p className="text-xs leading-relaxed">Send <strong>{money(a.stats.approved_cents)}</strong> to <strong>{a.paypal_email}</strong> from your PayPal balance now? This can't be undone.</p>
              <div className="flex gap-2">
                <button type="button" data-testid="amb-payout-send" disabled={busy === "payout"} onClick={payout} className={`${btn} flex-1 justify-center bg-[#007a3d] text-white hover:bg-[#0a0a0b]`}>{busy === "payout" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Confirm & send</button>
                <button type="button" data-testid="amb-payout-cancel" onClick={() => setConfirmPay(false)} className={`${btn} border border-[#e3e2dc] hover:border-[#0a0a0b]`}>Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" data-testid="amb-payout-button" disabled={a.stats.approved_cents <= 0} onClick={() => setConfirmPay(true)} className={`${btn} w-full justify-center bg-[#007a3d] text-white hover:bg-[#0a0a0b]`}>
              <Send size={13} /> Pay {money(a.stats.approved_cents)} via PayPal
            </button>
          )}
          <DeleteButton testId="amb-delete" label="Remove ambassador" title="Remove ambassador" confirmLabel="Remove" description={`Remove ${a.name}? Their login and unpaid commissions are deleted; paid records are kept.`} onConfirm={remove} />
        </div>
      </div>

      <div>
        <p className="font-display font-extrabold uppercase text-sm mb-3">Commission ledger</p>
        {a.commissions.length === 0 ? <p className="text-sm text-[#5f5e5a]">No referred orders yet.</p> : (
          <div className="border border-[#e3e2dc] rounded-2xl overflow-x-auto">
            <table className="w-full text-left min-w-[860px]">
              <thead><tr className="font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a] border-b border-[#e3e2dc]">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Pieces · price − cost − fee = profit</th><th className="px-4 py-3">Rate</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" />
              </tr></thead>
              <tbody>
                {a.commissions.map((c) => (
                  <tr key={c.id} data-testid={`amb-com-${c.order_ref}`} className="border-b border-[#e3e2dc] last:border-0 align-top">
                    <td className="px-4 py-3 font-mono-d text-xs whitespace-nowrap">{fmt(c.created_at)}</td>
                    <td className="px-4 py-3 font-mono-d text-xs">#{c.order_ref}<span className="block text-[#5f5e5a]">{c.customer_name || ""}</span></td>
                    <td className="px-4 py-3 text-xs">{c.lines.map((l, i) => <span key={i} className="block">{l.quantity}× {l.product_name} · {money(l.unit_amount)} − {money(l.cost_cents)} − {money(l.fee_cents)} = <strong>{money(l.profit_cents)}</strong></span>)}</td>
                    <td className="px-4 py-3 font-mono-d text-xs">{c.pct}%</td>
                    <td className="px-4 py-3 font-mono-d text-sm tabular-nums">{money(c.commission_cents)}</td>
                    <td className="px-4 py-3"><Pill status={c.status} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {c.status !== "paid" && (
                        <>
                          {c.status !== "approved" && <button type="button" data-testid={`amb-com-approve-${c.order_ref}`} disabled={busy === c.id} onClick={() => setCommission(c.id, "approved")} className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-[#007a3d] hover:underline mr-3">Approve</button>}
                          {c.status !== "void" && <button type="button" data-testid={`amb-com-void-${c.order_ref}`} disabled={busy === c.id} onClick={() => setCommission(c.id, "void")} className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-[#ff2a85] hover:underline">Void</button>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {a.payouts.length > 0 && (
        <div>
          <p className="font-display font-extrabold uppercase text-sm mb-3">Payouts</p>
          <div className="border border-[#e3e2dc] rounded-2xl divide-y divide-[#e3e2dc]">
            {a.payouts.map((p) => (
              <div key={p.id} data-testid={`amb-payout-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="text-xs"><span className="font-mono-d">{fmt(p.created_at)}</span> · {p.orders} order{p.orders !== 1 ? "s" : ""} · PayPal batch <span className="font-mono-d">{p.paypal_batch_id}</span>{p.error ? <span className="block text-[#ff2a85]">{p.error}</span> : null}</div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono-d text-[9px] uppercase tracking-[0.15em] rounded-full px-2.5 py-1 ${p.status === "success" ? "bg-[#007a3d]/10 text-[#007a3d]" : p.status === "denied" || p.item_status === "failed" ? "bg-[#ff2a85]/10 text-[#ff2a85]" : "bg-[#f3f2ec] text-[#5f5e5a]"}`}>{p.item_status || p.status}</span>
                  <span className="font-mono-d text-sm">{money(p.amount_cents)}</span>
                  <button type="button" data-testid={`amb-payout-refresh-${p.id}`} disabled={busy === p.id} onClick={() => refreshPayout(p.id)} className="h-8 w-8 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] flex items-center justify-center" title="Check PayPal status"><RefreshCw size={12} className={busy === p.id ? "animate-spin" : ""} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
